import { useEffect, useState, useCallback } from 'react'
import { isEqual } from 'lodash-es'
import { isJSON } from 'utils/helper'
import { uuidv4 } from 'utils/generic'

const TAB_ID_KEY = 'tab-id'

export function getTabId() {
  let tabId = sessionStorage.getItem(TAB_ID_KEY)

  if (!tabId) {
    tabId = uuidv4() || Math.random().toString(36).substring(2)
    sessionStorage.setItem(TAB_ID_KEY, tabId)
  }

  return tabId
}

const NonParsableKeys = ['token', 'LANGUAGE_SELECTION', 'isLoginMessageCleared']

const appVersion = process.env.REACT_APP_VERSION ?? 'default'
export const USE_SESSION_STORAGE_KEY = 'use-session-storage'
export const USE_SESSION_STORAGE_PERSIST_KEY = `${USE_SESSION_STORAGE_KEY}:${appVersion}:${getTabId()}`

const useSessionStorage = <T>(
  key: string,
  initialValue: T,
  setToString = true,
): [T, (value: T | ((previous: T) => T)) => void] => {
  // Get from session storage then
  // parse stored json or return initialValue

  const readValue = useCallback(() => {
    // Prevent build error "window is undefined" but keep keep working
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const entry = window.sessionStorage.getItem(USE_SESSION_STORAGE_PERSIST_KEY)
      const item = entry ? JSON.parse(entry)[key] : null

      if (!item) {
        return initialValue
      }

      if (NonParsableKeys.includes(USE_SESSION_STORAGE_PERSIST_KEY)) {
        return item
      }

      return isJSON(item) ? JSON.parse(item) : item
    } catch (error) {
      console.warn(`Error reading sessionStorage key “${USE_SESSION_STORAGE_PERSIST_KEY}”:`, error)
      return window.sessionStorage.getItem(USE_SESSION_STORAGE_PERSIST_KEY) ?? initialValue
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USE_SESSION_STORAGE_PERSIST_KEY, key])

  const [storedValue, setStoredValue] = useState<T>(readValue())

  const setValue = useCallback(
    (value: T) => {
      // Prevent build error "window is undefined" but keep keep working
      if (typeof window == 'undefined') {
        console.warn(
          `Tried setting sessionStorage key “${USE_SESSION_STORAGE_PERSIST_KEY}” even though environment is not a client`,
        )
      }

      try {
        const entry = sessionStorage.getItem(USE_SESSION_STORAGE_PERSIST_KEY)
        const currentData = entry && isJSON(entry) ? JSON.parse(entry) : {}

        // Allow value to be a function so we have the same API as useState
        const newValue = value instanceof Function ? value(storedValue) : value

        const newData = {
          ...currentData,
          [key]: setToString ? JSON.stringify(newValue) : newValue,
        }

        // Save to session storage
        window.sessionStorage.setItem(USE_SESSION_STORAGE_PERSIST_KEY, JSON.stringify(newData))

        // Save state
        setStoredValue(newValue)

        // We dispatch a custom event so every usesessionStorage hook are notified
        window.dispatchEvent(new Event('session-storage'))
      } catch (error) {
        console.warn(`Error setting sessionStorage key “${USE_SESSION_STORAGE_PERSIST_KEY}”:`, error)
      }
    },
    [storedValue, key, setToString],
  )

  const setSessionStorage: (value: T | ((previous: T) => T)) => void = useCallback(
    (val) => {
      if (typeof val === 'function') {
        setValue((val as (previous: T) => T)(storedValue))
        return
      }

      setValue(val)
    },
    [setValue, storedValue],
  )

  // Update stored value on key change
  useEffect(() => {
    setStoredValue(readValue())
  }, [readValue])

  // Add event listeners to listen to session storage change
  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue((prev) => {
        const newValue = readValue()

        if (isEqual(newValue, prev)) {
          return prev
        }

        return newValue
      })
    }

    // this only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange)

    // this is a custom event, triggered in writeValueTosessionStorage
    window.addEventListener('session-storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('session-storage', handleStorageChange)
    }
  }, [readValue])

  return [storedValue, setSessionStorage]
}

export const clearPreviousSessionStorage = () => {
  if (typeof window !== undefined) {
    Object.keys(window.sessionStorage).forEach((key) => {
      if (key.includes(USE_SESSION_STORAGE_KEY)) {
        sessionStorage.removeItem(key)
      }
    })
  }
}

export default useSessionStorage
